// Generated by bjt-data-sync script
// Do not edit manually

import { 
    Consumable, // Use Consumable instead of ConsumableItem
    ConsumableListData, 
    ConsumablePriceTier, // Use this as suggested
    ConsumableInventory, // Use this as suggested
    ConsumableFilterOptions // Keep this for ConsumableListData return type check
} from '../../types/consumables';
import { PriceTier } from '../../types/common';
import { getConsumablePrices as getBaseConsumablePrices } from './prices.mocks'; 
import { getConsumableInventory as getBaseConsumableInventory } from './inventory.mocks'; 
import { dynamicConsumableOptions, ConsumableFilterOptions_Local } from './consumableFilterOptions.mocks'; 
import { delay } from '../../utils/delay';
import rawConsumablesData from './data/consumables.data.json'; // Import JSON data

// Helper to get prices and map to ConsumablePriceTier
const getConsumablePrices = (productId: number | string, productLineId: number): ConsumablePriceTier[] => {
  const basePriceTiers: PriceTier[] = getBaseConsumablePrices(Number(productId), productLineId); 
  
  return basePriceTiers.map(baseTier => ({
    region: baseTier.region,
    currency: baseTier.currency,
    tiers: baseTier.tiers.map(t => ({ 
      min_quantity: t.min_quantity,
      max_quantity: t.max_quantity,
      price: t.base_price, 
      discount_rate: t.discount_rate,
    }))
  }));
};

// Helper to get inventory for a specific consumable
const getConsumableInventory = (productId: number | string, productLineId: number): ConsumableInventory[] => {
  return getBaseConsumableInventory(Number(productId), productLineId); 
};

// Assign imported data to the rawMockConsumables constant with type assertion
const rawMockConsumables: Omit<Consumable, 'prices' | 'inventory'>[] = rawConsumablesData as Omit<Consumable, 'prices' | 'inventory'>[];

// Process raw data into the full Consumable type
const mockConsumables: Consumable[] = rawMockConsumables.map(consumable => ({
  ...consumable,
  prices: getConsumablePrices(consumable.id, consumable.product_line_id),
  inventory: getConsumableInventory(consumable.id, consumable.product_line_id),
}));

// Function to get mock consumables with filtering and pagination
export const getMockConsumables = (options: {
    productLineId?: number | string, 
    region?: string,
    lang?: string,
    page?: number,
    pageSize?: number,
    filters?: Record<string, any>
} = {}, pageDeprecated = 1, pageSizeDeprecated = 10): ConsumableListData => {
  // Prioritize options object for pagination, handle deprecated direct params
  const page = options.page ?? pageDeprecated;
  const pageSize = options.pageSize ?? pageSizeDeprecated;
  const filters = options.filters ?? {};
  const productLineId = options.productLineId;
  const region = options.region ?? 'CN'; // Default region if needed for price/inventory lookup

  let filteredConsumables = [...mockConsumables]; // Start with processed consumables

  // --- Filter by Product Line ID ---
  if (productLineId !== undefined && productLineId !== null) {
    const numericProductLineId = Number(productLineId);
    filteredConsumables = filteredConsumables.filter(c => c.product_line_id === numericProductLineId);
  }
  
  // Apply other filters 
  if (filters.app_model && String(filters.app_model).toLowerCase() !== 'all') {
    const selectedModel = String(filters.app_model).trim().toLowerCase();
    filteredConsumables = filteredConsumables.filter(c => 
      c.app_model?.toLowerCase().replace(/"/g, '').split(',').map(am => am.trim()).includes(selectedModel)
    );
  }
  if (filters.bag_type && String(filters.bag_type).toLowerCase() !== 'all') {
    filteredConsumables = filteredConsumables.filter(c => c.bag_type?.toLowerCase() === String(filters.bag_type).toLowerCase());
  }
  if (filters.material && String(filters.material).toLowerCase() !== 'all') {
    filteredConsumables = filteredConsumables.filter(c => c.material?.toLowerCase() === String(filters.material).toLowerCase());
  }
  // Add other filters as needed (e.g., thickness, width, length if they are added to filters)

  const total = filteredConsumables.length;
  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (page - 1) * pageSize;
  const items: Consumable[] = filteredConsumables.slice(startIndex, startIndex + pageSize);

  // Use the imported dynamic options, typed with the local interface
  const finalFilterOptions = dynamicConsumableOptions; // This is ConsumableFilterOptions_Local

  return {
    items,
    total,
    page,
    page_size: pageSize,
    total_pages: totalPages,
    // Use type assertion for filterOptions as its structure comes from ConsumableFilterOptions_Local
    filterOptions: finalFilterOptions as unknown as ConsumableFilterOptions, 
  };
};

// Mock function to get a single consumable by ID
export const getMockConsumableById = (id: string | number): Consumable | undefined => {
  return mockConsumables.find(c => c.id.toString() === id.toString());
};
